"use client";

/**
 * The display state of a summary card: what text is on screen, whether it is
 * expanded, and what the progress line says.
 *
 * Two rules that are easy to break by accident and hard to see:
 *
 *  1. the summary lands EXPANDED when the reader just watched it stream in and
 *     COLLAPSED when it arrived from cache. `wasStreamingRef` is one-shot, so a
 *     later cached swap does not inherit the previous expansion;
 *  2. `onSummaryGenerated` fires once per distinct completed body, not once per
 *     render — it is guarded on the content actually changing.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import {
  briefingKindFor,
  initialStatusMessage,
  statusMessageForElapsed,
  type BriefingKind,
} from "./generation-narration";

/**
 * How often the progress ladder re-reads the clock. The rungs are whole
 * seconds, so a one-second tick is the coarsest interval that cannot skip one.
 */
const STATUS_TICK_MS = 1000;

export interface UseSummaryDisplayOptions {
  content: string | undefined;
  isLoading: boolean;
  isStreaming: boolean;
  /** The context reports no error as `undefined`; a caller may pass `null`. */
  error: string | null | undefined;
  entityType?: string;
  summaryType: string;
  onSummaryGenerated?: (content: string) => void;
  onError?: (message: string) => void;
}

export interface SummaryDisplay {
  briefingKind: BriefingKind;
  displayedContent: string;
  isExpanded: boolean;
  statusMessage: string;
  toggleExpanded: () => void;
  /** Reset the view before a (re)generation so no stale body lingers. */
  resetForGeneration: () => void;
}

export function useSummaryDisplay({
  content,
  isLoading,
  isStreaming,
  error,
  entityType,
  summaryType,
  onSummaryGenerated,
  onError,
}: UseSummaryDisplayOptions): SummaryDisplay {
  const [displayedContent, setDisplayedContent] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(null);
  const [statusMessage, setStatusMessage] = useState("");
  // Tracks whether the current summary was produced by a live stream in this
  // view (vs. a cached/instant load) so we can keep it expanded when it lands.
  const wasStreamingRef = useRef(false);

  const briefingKind = briefingKindFor(entityType, summaryType);

  // Progressive status messages during generation.
  //
  // The ladder is TIME-driven, so it needs its own clock: this effect re-runs
  // only when its inputs change, and generation is one long quiet stretch with
  // no re-render to ride on. Without the interval the rung was computed once,
  // at ~0 seconds elapsed, and the card showed the first message for the whole
  // generation — every rung after it was unreachable.
  useEffect(() => {
    if (!isLoading && !isStreaming) {
      if (content) {
        setStatusMessage("");
        setGenerationStartTime(null);
      }
      return;
    }
    if (generationStartTime === null) {
      setGenerationStartTime(Date.now());
      setStatusMessage(initialStatusMessage(briefingKind));
      return;
    }
    const advance = () => {
      const elapsed = Math.floor((Date.now() - generationStartTime) / 1000);
      const next = statusMessageForElapsed(briefingKind, elapsed);
      // `null` below the first rung: leave the current line alone rather than
      // blanking it — the ladder only moves forward.
      if (next) setStatusMessage(next);
    };
    advance();
    const timer = setInterval(advance, STATUS_TICK_MS);
    return () => clearInterval(timer);
  }, [isLoading, isStreaming, generationStartTime, content, briefingKind]);

  // Streaming content, then the completed hand-off.
  useEffect(() => {
    if (!content) {
      setDisplayedContent("");
      return;
    }
    if (isStreaming) {
      // Direct update for streaming content.
      wasStreamingRef.current = true;
      setDisplayedContent(content);
      return;
    }
    if (displayedContent !== content) {
      setDisplayedContent(content);
      onSummaryGenerated?.(content);
      // Keep it expanded when the user just watched it stream in; collapse to
      // the teaser for cached/auto-generated loads.
      setIsExpanded(wasStreamingRef.current);
      wasStreamingRef.current = false;
    }
  }, [content, isStreaming, displayedContent, onSummaryGenerated]);

  useEffect(() => {
    if (error) onError?.(error);
  }, [error, onError]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((current) => !current);
  }, []);

  const resetForGeneration = useCallback(() => {
    setIsExpanded(false);
    setDisplayedContent("");
  }, []);

  return {
    briefingKind,
    displayedContent,
    isExpanded,
    statusMessage,
    toggleExpanded,
    resetForGeneration,
  };
}
